import { useEffect, useRef } from 'react';
import { ChevronDownIcon, ExternalLinkIcon } from 'lucide-react';
import { ContentEditor, type FieldOverrides, type Json } from './ContentEditor';
import { fieldLabels, humanize } from '../adminLabels';
import { plural } from '../../core/format/text';
type Props = {
  value: Json;
  onChange: (value: Json) => void;
  /** Blok dat open moet (bijv. na een zoekresultaat). */
  focusKey: string | null;
  /** Telt op bij elke zoekkeuze, zodat hetzelfde blok opnieuw kiezen weer opent en scrollt. */
  focusTick?: number;
  /**
   * Vaste opbouw van de pagina (bijv. de homepage): volgorde, titel, rol in het verhaal en een
   * link naar precies dat blok op de site. Blokken die hier niet in staan, komen erachter.
   */
  blocks?: readonly ContentBlock[];
};
export type ContentBlock = {
  key: string;
  title: string;
  role: string;
  href?: string;
  /** Toon van het vlak op de site (sand, shell, blush, sage, clay). */
  tone?: string;
  /** Hoe de velden in dit blok op díe plek heten. */
  labels?: FieldOverrides;
};
const TONE_NAMES: Record<string, string> = {
  sand: 'zand',
  shell: 'licht',
  blush: 'blush',
  sage: 'salie',
  clay: 'klei'
};
const TONE_SWATCH: Record<string, string> = {
  sand: 'bg-sand',
  shell: 'bg-shell',
  blush: 'bg-blush',
  sage: 'bg-sage',
  clay: 'bg-clay'
};
function ViewLink({
  href
}: {
  href: string;
}) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-1.5 text-[12px] text-clay underline underline-offset-2 hover:text-ink" data-id="element-5184" data-mp="9ulj9zvrctru">
      Bekijk dit blok
      <ExternalLinkIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-5185" data-mp="xl8ryndhznfg" />
      <span className="sr-only" data-id="element-5186" data-mp="xj4p88rc4ugr"> (opent in een nieuw tabblad)</span>
    </a>;
}

/** Nummer, titel, rol en toon van een blok: zo herken je het op de site. */
function BlockTitle({
  block,
  number
}: {
  block: ContentBlock;
  number: number;
}) {
  return <>
      <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-shell text-[12px] tabular-nums text-ink" data-id="element-5187" data-mp="2uf2fi3nh12fc">
        {number}
      </span>
      <span className="min-w-0 flex-1" data-id="element-5188" data-mp="138xp2kicsypk">
        <span className="block text-[14px] font-medium text-ink" data-id="element-5189" data-mp="13btka12th9ab">
          <span className="sr-only" data-id="element-5190" data-mp="17a9i700fs22i">Blok {number}: </span>
          {block.title}
          <span className="font-normal text-muted" data-id="element-5191" data-mp="1canpgfu7r7zh"> · {block.role}</span>
        </span>
        {block.tone && <span className="mt-0.5 inline-flex items-center gap-1.5 text-[12px] text-muted" data-id="element-5192" data-mp="1h8fi5c2omy4q">
            <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ring-1 ring-line ${TONE_SWATCH[block.tone] ?? ''}`} data-id="element-5193" data-mp="1m7jntsqeedqb" />
            Op {TONE_NAMES[block.tone] ?? block.tone}
          </span>}
      </span>
    </>;
}
const isObject = (v: Json): v is {
  [key: string]: Json;
} => typeof v === 'object' && v !== null && !Array.isArray(v);

/** De kop van een blok zoals hij op de site staat, als herkenning in de dichte balk. */
export function previewOf(v: Json): string {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.length ? plural(v.length, 'item', 'items') : 'Leeg';
  if (isObject(v)) {
    const heading = v.heading;
    if (isObject(heading) && typeof heading.text === 'string' && heading.text) return heading.text;
    for (const key of ['title', 'name', 'eyebrow', 'text', 'body']) {
      const x = v[key];
      if (typeof x === 'string' && x) return x;
    }
  }
  return '';
}

/**
 * Een pagina als rij dichtgeklapte blokken, in de volgorde van de site. Eén lang formulier
 * van tachtig velden is niet te overzien; zo zie je eerst de opbouw en open je alleen het
 * blok dat je wilt aanpassen. Losse tekstregels bovenaan de pagina blijven gewoon open.
 */
export function ContentSections({
  value,
  onChange,
  focusKey,
  focusTick,
  blocks
}: Props) {
  const refs = useRef<Record<string, HTMLDetailsElement | null>>({});
  useEffect(() => {
    if (!focusKey) return;
    const el = refs.current[focusKey];
    if (!el) return;
    el.open = true;
    el.scrollIntoView({
      block: 'start',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }, [focusKey, focusTick]);

  // Klantcitaten e.d. zijn één lijst: die heeft geen blokken.
  if (!isObject(value)) return <ContentEditor value={value} onChange={onChange} data-id="element-5194" data-mp="2ncnk41bufbln" />;
  const visible = Object.entries(value).filter(([k]) => !fieldLabels[k]?.hidden);
  const position = (key: string) => {
    const at = blocks?.findIndex(b => b.key === key) ?? -1;
    return at === -1 ? Number.MAX_SAFE_INTEGER : at;
  };
  const entries = blocks ? [...visible].sort(([a], [b]) => position(a) - position(b)) : visible;
  return <div className="space-y-3" data-id="element-5195" data-mp="367cc7nehxrje">
      {entries.map(([key, v], i) => {
      const block = blocks?.find(b => b.key === key);
      const number = block ? position(key) + 1 : null;
      const set = (next: Json) => onChange({
        ...value,
        [key]: next
      });
      // Losse tekstregels (een kop, een zin): open, met dezelfde titel en link als een blok.
      if (typeof v !== 'object' || v === null || Array.isArray(v) && block) {
        return <div key={key} className="rounded-md border border-line bg-white" data-id="element-5196" data-mp="1mc7wjhxleztq">
              {block && number !== null && <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3" data-id="element-5197" data-mp="211dsmx14w9xe">
                  <BlockTitle block={block} number={number} data-id="element-5198" data-mp="1w3uf5zc9g16i" />
                  {block.href && <ViewLink href={block.href} data-id="element-5199" data-mp="18ejd8ej6c0qs" />}
                </div>}
              <div className="p-4" data-id="element-5200" data-mp="13f6pzsoe4rno">
                <ContentEditor fieldKey={key} value={v} onChange={set} override={block ? {
              label: block.labels?.[key]?.label ?? 'Tekst',
              hint: block.labels?.[key]?.hint
            } : undefined} data-id="element-5201" data-mp="13g811x5epe5e" />
              </div>
            </div>;
      }
      const preview = previewOf(v);
      return <details key={key} ref={el => {
        refs.current[key] = el;
      }} open={i === 0} className="group scroll-mt-24 rounded-md border border-line bg-white open:shadow-sm" data-id="element-5202" data-mp="2tozv21yml8io" data-mpi={i}>
            <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3.5 [&::-webkit-details-marker]:hidden" data-id="element-5203" data-mp="30thqlh54e8ut">
              {block && number !== null ? <span className="flex min-w-0 flex-1 items-start gap-3" data-id="element-5204" data-mp="3as6yqi77sexv">
                  <BlockTitle block={block} number={number} data-id="element-5205" data-mp="3aq4d0ad3h7yy" />
                </span> : <span className="min-w-0 flex-1" data-id="element-5206" data-mp="2d2c4ik8l6kc2">
                  <span className="block text-[14px] font-medium text-ink" data-id="element-5207" data-mp="2i2q91sazbc5c">{humanize(key)}</span>
                  {/* Een pagina mét opbouw, maar dit veld staat er niet in: zeg dat eerlijk (data/pageSections.test.ts houdt het leeg). */}
                  {blocks ? <span className="mt-0.5 block text-[12px] text-muted" data-id="element-5208" data-mp="1udytq1ihmo9z">Staat (nog) niet op de site</span> : preview && <span className="mt-0.5 block truncate text-[12px] text-muted" data-id="element-5209" data-mp="1zbqt1glgpco7">{preview}</span>}
                </span>}
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted transition-transform duration-150 ease-soft group-open:rotate-180" strokeWidth={1.5} aria-hidden="true" data-id="element-5210" data-mp="cgc3e4jcmeet" />
            </summary>
            <div className="border-t border-line p-4" data-id="element-5211" data-mp="2hmpsnyhjg4d">
              {(fieldLabels[key]?.hint || block?.href) && <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-5212" data-mp="3tqjwufaqmobt">
                  {fieldLabels[key]?.hint ? <p className="max-w-prose text-[12px] text-muted" data-id="element-5213" data-mp="3kvvtwvsmxiq6">{fieldLabels[key]?.hint}</p> : <span data-id="element-5214" data-mp="3ktrg9m4xp5cp" />}
                  {block?.href && <ViewLink href={block.href} data-id="element-5215" data-mp="3fxu3fpsvqh4o" />}
                </div>}
              {preview && block && <p className="mb-4 truncate text-[12px] text-muted" data-id="element-5216" data-mp="3awwkvc3yjl2n">Nu op de site: {preview}</p>}
              <ContentEditor fieldKey={key} value={v} onChange={set} depth={0} labels={block?.labels} data-id="element-5217" data-mp="35zwcuvnjs88w" />
            </div>
          </details>;
    })}
    </div>;
}